import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { LoaderService } from 'app/modules/shared/components/loader/loader.service';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { MetaDataService } from 'app/modules/shared/services/meta-data.service';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';
import { RestApiService } from 'app/modules/shared/services/rest-api.service';
import * as _ from 'lodash';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TabViewModule } from 'primeng/tabview';
import { AppIcons } from '../../../shared/app.icons';
import { AppSettings } from '../../../shared/app.settings';
import { MITableComponent } from '../../../shared/components/mi-table/mi-table.component';
import { ModuleHeaderComponent } from '../../../shared/components/module-header/module-header.component';
import type { Country } from '../../../shared/models/country';
import type { Language } from '../../../shared/models/language';
import { CommonBindingDataService } from '../../../shared/services/common-binding-data.service';
import { ConfigService } from '../../../shared/services/config.service';
import { EntityService } from '../../../shared/services/entity.service';
import type { EntityCountsResponse, EntityList } from '../../models/entity.models';
import { ListResponse } from '../../models/listResponse.models';
import { ToastModule } from 'primeng/toast';
import {
  TrialColumn,
  TrialData,
  TrialPagination,
  TrialButton,
  TrialFilterField,
  TrialProtocolData,
  TrialMultiApiPayload,
  TrialTableView,
  TrialEntityCountPayload,
  TrialTabViewEvent,
  TrialFilterEvent,
  TrialPageChangeEvent,
  TrialRowSelectEvent,
  TrialFileUploadEvent,
  TrialCurrentRow,
  TrialAttributeLabels,
  TrialFilterAttributeLabels,
  TrialDataValues
} from '../../models/trials';
import dayjs from 'dayjs';
import { StorageService } from 'app/modules/shared/services/storage.service';

@Component({
  selector: 'app-trials-list',
  standalone: true,
  imports: [BreadcrumbModule, ModuleHeaderComponent, TabViewModule, TranslateModule, MITableComponent, MITableComponent, AccessProviderDirective, ToastModule],
  templateUrl: './trials-list.component.html',
  styleUrl: './trials-list.component.scss'
})
export class TrialsListComponent implements OnInit {
  @ViewChild('uploader', { static: true }) uploader: ElementRef<HTMLElement>;
  @ViewChild(MITableComponent) tableRef: MITableComponent;

  miIcons = AppIcons;
  activeIndex: number = 0;
  isLoading = true;
  filterAvailableColumns: TrialColumn[] = [];
  cols: TrialColumn[] = [];
  moduleName = 'protocol';
  filterList: TrialColumn[];
  btnLabel: string;
  items: MenuItem[] | undefined;
  isShowMenu: boolean = true;
  attributeLabels: TrialAttributeLabels = {};
  filterAttributeLabels: TrialFilterAttributeLabels = {};
  listColumns: TrialDataValues;
  allColumns: TrialColumn[];
  pagination: TrialPagination;
  totalRecords: number = 0;
  tableData: TrialData[] = [];
  buttons: TrialButton[] = [];
  country: Country;
  language: Language;
  entityCount: EntityCountsResponse;
  itemsPerPage: number;
  entityData: EntityList = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    advanceFilters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: "",
    actionStatus: ''
  };
  routePath: MenuItem[] = [];
  deleteTrialMsg: string;
  deleteTrialHeaderMsg: string;
  showMenu: boolean = false;
  moduleHeaderSecondaryBtnMenuItems: MenuItem[] = [];
  filterFieldsArray: TrialFilterField[] = [];
  isReady: boolean = false;
  attributeData: TrialTableView;
  data: TrialTableView;
  rowItems: MenuItem[] | undefined;
  bulkSelectionsItems: MenuItem[] | undefined;
  currentRow: TrialCurrentRow;
  visiblePrintLabel: boolean = false;
  showBulkUpdateDialog: boolean = false;
  visible: boolean = false;
  bulkUpdateHeader: string;
  reviewHeader: string;
  btnUpdate: string;
  entityCreatedAtAttributeId: string;
  isEntity = true;
  protocolStatusAttributeId: string;
  showImportExportBtn: boolean = false;
  advanceFilterAttributeCodes = ['start_date', 'end_date'];

  isTabAllAllowed: boolean = false;
  isTabDraftAllowed: boolean = false;
  isTabCompletedAllowed: boolean = false;
  isTabRunningAllowed: boolean = false;
  isTabInactiveAllowed: boolean = false;
  protocolStatusObject: any;
  startDateAttributeId: string;
  endDateAttributeId: string;

  constructor(private trialService: EntityService,
    private cs: CommonBindingDataService, private confirmationService: ConfirmationService,
    private router: Router, private messageService: MessageService, private actRoute: ActivatedRoute,
    private metaDataService: MetaDataService,
    private restApiService: RestApiService,
    private configService: ConfigService, private permissionAccessService: PermissionAccessService,
    private cd: ChangeDetectorRef, private loaderService: LoaderService, private storageService: StorageService,
  ) { }

  ngOnInit() {
    this.initializeLabels();
    this.initializeRowItems();
    this.initializeModuleHeaderSecondaryBtnMenuItems();
    this.initializeOtherVariablesAndAPI();
    this.getMetaData();
    this.checkPermission();
    this.restoreActiveTab();
    this.handleUrlParams();
  }

  checkPermission() {
    this.isTabAllAllowed = this.permissionAccessService.hasAccess('PROTOCOLS', '', 'ALL', 'LIST');
    this.isTabDraftAllowed = this.permissionAccessService.hasAccess('PROTOCOLS', '', 'DRAFT', 'LIST');
    this.isTabCompletedAllowed = this.permissionAccessService.hasAccess('PROTOCOLS', '', 'COMPLETED', 'LIST');
    this.isTabInactiveAllowed = this.permissionAccessService.hasAccess('PROTOCOLS', '', 'INACTIVE', 'LIST');
    this.isTabRunningAllowed = this.permissionAccessService.hasAccess('PROTOCOLS', '', 'DRAFT', 'LIST');
  }

  getMetaData() {
    this.getEntityCount();
    const payload = this.getMultiApiPayload();
    this.metaDataService.metaDataMultipleApi(payload).subscribe((res) => {
      const advancedFilter = JSON.parse(res.advancedFilter.responseBody);
      if (advancedFilter) {
        this.setTrialFilterView(advancedFilter);
      }

      const sort = JSON.parse(res.sort.responseBody);
      const startDate = sort.tabs[0].groups[0].fields.find(field => field.attributeCode === 'start_date');
      this.startDateAttributeId = startDate.attributeId;
      const endDate = sort.tabs[0].groups[0].fields.find(field => field.attributeCode === 'end_date');
      this.endDateAttributeId = endDate.attributeId;

      const defaultTableView = JSON.parse(res.defaultTableView.responseBody);
      if (defaultTableView) {
        this.setProtocolTableView(defaultTableView);
      }
      this.cs.applyFilterToColumn(defaultTableView, sort)
    });
  }

  private getMultiApiPayload(): TrialMultiApiPayload[] {
    const viewCode = this.configService.isSiteUser() ? AppSettings.VIEW_CODE.PROTOCOL_LIST_SITE_VIEW : AppSettings.VIEW_CODE.DEFAULT_TABLE_VIEW;
    return [
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.ADVANCED_FILTER_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.ADVANCED_FILTER,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${AppSettings.VIEW_CODE.DEFAULT_SORT_VIEW}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.SORT,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
      {
        url: `${AppSettings.BASE_URL}${this.configService.getLoggedInTenantCode()}/api/v1/entity-conf/${AppSettings.ENTITY_CODE.PROTOCOL}/attributes?forTenantCode=${this.configService.getForTenantCode()}&viewCode=${viewCode}`,
        inputBody: '',
        method: 'GET',
        outputKey: AppSettings.META_OUTPUT_KEY.DEFAULT_TABLE_VIEW,
        headers: this.restApiService.getHeaderForMultipleAPI()
      },
    ]
  }

  private initializeRowItems() {
    const isEdit = this.permissionAccessService.hasAccess('PROTOCOLS', '', 'ALL', 'EDIT');
    const isActivate = this.permissionAccessService.hasAccess('PROTOCOLS', '', 'ALL', 'ACTIVATE');
    this.rowItems = [
      ...(isEdit ? [{
        label: this.cs.getLabel('protocol.lbl_edit'),
        icon: `${AppIcons.EDIT_2} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'edit',
        command: () => {
          this.update();
        }
      }] : []),
      ...(isActivate ? [{
        label: this.cs.getLabel('protocol.lbl_activate'),
        icon: `${AppIcons.CIRCLE_CHECKED} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'active',
        command: () => {
          this.onUpdateStatus('Running', this.cs.getLabel('protocol.lbl_activate'));
        }
      }] : []),
      ...(isActivate ? [{
        label: this.cs.getLabel('protocol.lbl_deactivate'),
        icon: `${AppIcons.ACTIVATE} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'deactive',
        command: () => {
          this.onUpdateStatus('Inactive', this.cs.getLabel('protocol.lbl_deactivate'));
        }
      }] : []),
    ];
  }

  onUpdateStatus(status: string, acceptLabelText: string) {
    const isActive = status === 'Running';
    this.confirmationService.confirm({
      header: this.cs.getLabel(isActive ? 'protocol.lbl_activate_confirmation' : 'protocol.lbl_deactivate_confirmation'),
      message: this.cs.getLabel(isActive ? 'protocol.msg_activate_confirmation' : 'protocol.msg_deactivate_confirmation'),
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectButtonStyleClass: 'bg-white text-color cancel',
      acceptButtonStyleClass: 'bg-red-500 delete',
      acceptLabel: acceptLabelText,
      rejectLabel: this.cs.getLabel('lbl_cancel'),
      accept: () => {
        const protocolData: TrialProtocolData = {
          forTenantCode: this.configService.getForTenantCode(),
          entityCode: this.moduleName,
          countryCode: this.country[0].countryCode,
          languageCode: this.language?.[0]?.langCode || 'en',
          data: [{
            attributeId: this.protocolStatusAttributeId,
            attributeValue: status
          }],
          relationshipData: [],
          viewCode: AppSettings.VIEW_CODE.PROTOCOL_STATUS_UPDATE_VIEW,
        };

        this.trialService.saveAttributeData(protocolData.entityCode, this.currentRow.id, protocolData).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: this.cs.getLabel('protocol.lbl_status_updated_successfully'),
            });
            setTimeout(() => {
              this.searchEntity();
              this.getEntityCount();
            }, 300);
          }
        });
      },
      reject: () => {
      }
    });
  }

  private initializeModuleHeaderSecondaryBtnMenuItems() {
    this.moduleHeaderSecondaryBtnMenuItems = [
    ];
  }

  private initializeLabels() {
    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.bulkUpdateHeader = this.cs.getLabel('protocol.lbl_update_protocol');
    this.reviewHeader = this.cs.getLabel('protocol.lbl_review_protocol');
    this.btnUpdate = this.cs.getLabel('protocol.lbl_update_protocol');
    this.routePath = [{ label: "Protocols", routerLink: 'trials/list' }];
  }

  private initializeOtherVariablesAndAPI() {
    this.btnLabel = this.cs.getLabel('protocol.lbl_add_new_protocol');
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.deleteTrialMsg = this.cs.getLabel('protocol.message.confirm_delete_msg');
    this.deleteTrialHeaderMsg = this.cs.getLabel('protocol.message.confirm_delete_header');
  }

  public onChange(event: TrialFileUploadEvent) {
    for (const file of event.target.files) {
      if (file) {
        if (file.size > AppSettings.FILE_UPLOAD_MAX_SIZE_IN_BYTE) {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: this.cs.getLabel('err_file_size_exceed_msg') });
        } else {
          const formData = new FormData();
          formData.append("file", file);
          formData.append('forTenantCode', this.configService.getForTenantCode());
          formData.append('entityCode', AppSettings.ENTITY_CODE.PROTOCOL);
          formData.append('viewCode', AppSettings.VIEW_CODE.EXCEL_IMPORT_VIEW);
          this.cs.importEntity(formData, AppSettings.ENTITY_CODE.PROTOCOL).subscribe(() => {
            this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('lbl_file_uploaded_successfully') });
          })
        }
      }
    }
  }

  setCurrentRowData(event: TrialCurrentRow) {
    const protocolStatusKey = Object.keys(event.protocol_status || {})[0] || null;
    this.cs.setActionStatus(protocolStatusKey);
    this.currentRow = event;

    if (event.deleted) {
      this.rowItems.forEach(item => {
        if (!item.separator) {
          item.disabled = true;
        }
      });
      return;
    }

    const getCurrentProtocolStatus = (protocolStatus: TrialCurrentRow['protocol_status']): string => {
      if (!protocolStatus) return '';

      for (const [status, value] of Object.entries(protocolStatus)) {
        if (value && value === status) {
          return status.toLowerCase();
        }
      }
      return '';
    };

    const currentStatus = getCurrentProtocolStatus(event.protocol_status);

    this.rowItems.forEach(item => { 
      switch (item.value) {
        case 'active':
          item.disabled = event.actionStatus === 'DELETED' || event.actionStatus === ''
            currentStatus === 'completed' ||
            currentStatus === 'running';
          break;
        case 'deactive':
          item.disabled = currentStatus === 'inactive' ||
            event.actionStatus === 'DELETED' ||
            currentStatus === 'completed';
          break;
        case 'edit':
          item.disabled = event.actionStatus === 'DELETED' ||
            currentStatus === 'completed' ||
            currentStatus === 'running' ||
            currentStatus === 'inactive';
          break;
      }
    });
  }

  update() {
    this.onUpdateTrial(this.currentRow.id);
  }

  setProtocolTableView(res: TrialTableView) {
    this.isLoading = false;
    this.data = res;
    this.protocolStatusAttributeId = res.tabs[0].groups[0].fields.find(field => field.attributeCode === 'protocol_status')?.attributeId;

    this.attributeLabels = this.cs.getAttributeLabels(this.data) as unknown as TrialAttributeLabels;
    this.allColumns = this.cs.getColumns(this.data).filter(column =>
      !['entity_created_at'].includes(column.attributeCode)
    );

    this.searchEntity();
    this.allColumns?.forEach((key, index) => {
      this.allColumns[index] = key;
      this.allColumns[index].field = key.attributeCode;
      this.allColumns[index].header = this.cs.getLabelValue(
        `protocol.fields.${key.attributeCode}.label`,
        this.attributeLabels,
        key.attributeCode
      );
    });
    this.filterAvailableColumns = _.clone(this.allColumns);
  }

  setTrialFilterView(filterResponse: any) {
    this.filterAttributeLabels = this.cs.getAttributeLabels(filterResponse) as unknown as TrialFilterAttributeLabels;
    this.filterList = this.cs.getColumns(filterResponse);
    this.filterList = _.sortBy(this.filterList, 'attributeIndex');
    if (this.configService.isSiteUser() || this.configService.isSponsor()) {
      this.filterList = this.filterList.filter(filter =>
        filter.attributeCode !== 'end_date' && filter.attributeCode !== 'sponsor_entity_id');
    } else {
      this.filterList = this.filterList.filter(filter =>
        filter.attributeCode !== 'end_date');

    }
    this.filterList = this.filterList.map((filter: any) => {
      if (filter.attributeCode === 'start_date') {
        filter.inputCode = 'dateRange';
      }
      if (filter.attributeCode === 'sponsor_entity_id') {
        filter.inputCode = 'entitySelectSearch';
        filter.moduleName = 'sponsor';
      }
      return filter;
    });


    if (this.configService.isSponsor() || this.configService.isSiteUser()) {
      const protocolStatusFilter = this.filterList.find(filter => filter.attributeCode === 'protocol_status') as any;
      if (protocolStatusFilter && protocolStatusFilter.presetValues) {
        protocolStatusFilter.presetValues = protocolStatusFilter.presetValues.filter(
          (preset: any) => preset.labelKey !== 'Draft'
        );

        const protocolStatusAttributeId = protocolStatusFilter.attributeId;
        const protocolStatusValues = protocolStatusFilter.presetValues.map((preset: any) => preset.labelKey);
        this.protocolStatusObject = {
          attributeId: protocolStatusAttributeId,
          attributeValue: protocolStatusValues
        };
        this.entityData.filters.push(this.protocolStatusObject);
      }
    }

  }

  onSortData(sortColumnDetails) {
    this.entityData.defaultSortColumn = sortColumnDetails.attributeId;
    this.entityData.defaultSortType = (sortColumnDetails.sortOrder === 1) ? AppSettings.ASC_SORT_ORDER : AppSettings.DESC_SORT_ORDER;
    this.searchEntity();
  }

  searchEntity() {
    this.entityData.countryCode = AppSettings.HEADER_COUNTRY_CODE;
    this.loaderService.hide();
    this.trialService.searchEntity(AppSettings.ENTITY_CODE.PROTOCOL, this.entityData, false).subscribe((res: ListResponse) => {
      this.listColumns = res?.data[0]?.values as unknown as TrialDataValues;
      this.pagination = res?.pagination as unknown as TrialPagination;
      this.totalRecords = res?.count;
      this.tableData = [];
      res.data?.forEach(data => {
        const protocolStatus = (data.values as unknown as TrialDataValues)?.protocol_status;
        this.tableData.push({
          id: data.id,
          actionStatus: (data.values as unknown as TrialDataValues)?.actionStatus || '',
          protocol_status: protocolStatus || {},
          ...data.values,
        });
      });
      this.loaderService.hide();
    });
  }

  onAddTrial() {
    this.router.navigate(['app/trials/add-trial',]);
  }

  onPageChange(event: TrialPageChangeEvent) {
    this.entityData.offset = event?.first;
    this.entityData.limit = event?.rows;
    this.searchEntity();
  }

  itemPerPageChange(event: number) {
    this.itemsPerPage = event;
    this.entityData.limit = this.itemsPerPage;
    this.searchEntity();
  }

  onTrialDelete(event: string) {
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      countryCode: AppSettings.HEADER_COUNTRY_CODE,
      entityCode: AppSettings.ENTITY_CODE.PROTOCOL,
      entityIds: [
        event
      ]
    };
    this.trialService.deleteEntityActionStatus(requestBody).subscribe({
      next: () => {
        this.cs.onDisplayMessage(this.data, 'protocol', AppSettings.MSG_ACTION.DELETE, 'success');
        setTimeout(() => {
          this.searchEntity();
          this.getEntityCount();
        }, 300);
      },
      error: () => {
        this.cs.onDisplayMessage(this.data, 'protocol', AppSettings.MSG_ACTION.DELETE, 'error');
      }
    })
  }

  onUpdateTrial(trialId: string) {
    this.router.navigate([`app/trials/add-trial/${trialId}`]);
  }

  onRowSelect(event: TrialRowSelectEvent) {
    if (event.type === 'row') {
      this.storageService.setItem('trialActiveIndex', this.activeIndex);
      const tabParam = this.getTabParamFromIndex(this.activeIndex);
      const detailTabParam = this.getDetailTabParamFromIndex(0);
      this.router.navigate(['app/trials/trial-details'], {
        queryParams: {
          tab: detailTabParam,
          id: event.data.id,
          fromTab: tabParam
        }
      });
      this.trialService.entityCurrentRow.next(event.data);
    }
  }

  onFilterValueChange(event: TrialFilterEvent) {
    const vData = event;
    this.entityData.filters = [];
    this.entityData.advanceFilters = [];
    for (const [key, value] of Object.entries(vData)) {
      const attributeValue = value;
      if (this.advanceFilterAttributeCodes.includes(key)) {
        this.setAdvanceFilter(key, value);
      } else if (attributeValue) {
        this.entityData.filters.push({
          attributeCode: key,
          attributeValue
        });
      }
    }

    if (this.configService.isSponsor() || this.configService.isSiteUser()) {
      this.entityData.filters.push(this.protocolStatusObject);
    }
    this.entityData.filters = _.filter(_.uniq(this.entityData.filters, (item) => {
      if (item.attributeValue.length > 0) {
        return item.attributeCode;
      }
    }), (element) => {
      if (element.attributeValue.length > 0) {
        return true;
      }
      return false;
    });
    this.entityData.offset = event.first;
    this.entityData.filters = this.cs.mapAttributeIds(this.entityData.filters, this.filterList, 'filter');
    this.searchEntity();
  }

  setAdvanceFilter(key: string, value: unknown) {
    if (value) {
      if (key === 'start_date') {
        this.entityData.advanceFilters = [];
        const endDate = dayjs(value[1]).endOf('day').valueOf() ? dayjs(value[1]).endOf('day').valueOf() : dayjs(value[0]).startOf('day').valueOf();
        this.entityData.advanceFilters.push({
          attributeId: Number(this.startDateAttributeId),
          fromValue: this.metaDataService.setDateFilterOffset(dayjs(value[0]).startOf('day').valueOf()),
          comparisonOperator: AppSettings.COMPARISON_OPERATOR.GTE
        }, {
          attributeId: Number(this.endDateAttributeId),
          fromValue: this.metaDataService.setDateFilterOffset(endDate),
          comparisonOperator: AppSettings.COMPARISON_OPERATOR.LTE
        });
      }
    }
  }

  onSearchValueChanges(event: string) {
    this.entityData.searchStr = event;
    this.searchEntity();
  }

  getEntityCount() {
    const country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    const entityCountPayload: TrialEntityCountPayload = {
      forTenantCode: this.configService.getForTenantCode(),
      entityCode: AppSettings.ENTITY_CODE.PROTOCOL,
      countryCode: <string>country[0].countryCode,
    };

    this.trialService.getEntityCount(AppSettings.ENTITY_CODE.PROTOCOL, entityCountPayload, false).subscribe((res: EntityCountsResponse) => {
      this.entityCount = res;
      if (this.configService.isSponsor() || this.configService.isSiteUser()) {
        this.entityCount.counts.ALL = this.entityCount.counts.ALL - this.entityCount.counts.DRAFT;
      }
    })
  }

  tabViewChange(event: TrialTabViewEvent) {
    this.activeIndex = event.index;
    this.storageService.setItem('trialActiveIndex', event.index);
    this.updateUrlWithTab(event.index);
    switch (event.index) {
      case 0:
        this.entityData.actionStatus = '';
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        this.tableRef?.clearFilter();
        this.searchEntity();
        break;
      case 1:
        this.entityData.actionStatus = 'DRAFT';
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        this.tableRef?.clearFilter();
        this.searchEntity();
        break;
      case 2:
        this.entityData.actionStatus = 'RUNNING';
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        this.tableRef?.clearFilter();
        this.searchEntity();
        break;
      case 3:
        this.entityData.actionStatus = 'COMPLETED';
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        this.tableRef?.clearFilter();
        this.searchEntity();
        break;
      case 4:
        this.entityData.actionStatus = 'INACTIVE';
        this.entityData.deleted = AppSettings.DELETED_TYPE.ONLY_NON_DELETED;
        this.entityData.filters = [];
        this.tableRef?.clearFilter();
        this.searchEntity();
        break;
      default:
        break;
    }
    if (event.index === 0 || event.index === 1 || event.index === 2) {
      this.showBulkUpdateDialog = false;
    }
  }

  private restoreActiveTab() {
    const savedIndex = this.storageService.getItem('trialActiveIndex');
    if (savedIndex !== null && savedIndex !== undefined) {
      this.activeIndex = parseInt(savedIndex);
      this.tabViewChange({ index: this.activeIndex });
    }
  }

  private handleUrlParams() {
    this.actRoute.queryParams.subscribe(params => {
      const tabParam = params.tab;
      if (tabParam) {
        const tabIndex = this.getTabIndexFromParam(tabParam);
        if (tabIndex !== -1 && tabIndex !== this.activeIndex) {
          this.activeIndex = tabIndex;
          this.tabViewChange({ index: this.activeIndex });
        }
      }
    });
  }

  private getTabIndexFromParam(tabParam: string): number {
    switch (tabParam.toLowerCase()) {
      case 'all':
        return 0;
      case 'draft':
        return 1;
      case 'active':
        return 2;
      case 'completed':
        return 3;
      case 'inactive':
        return 4;
      default:
        return -1;
    }
  }

  private updateUrlWithTab(tabIndex: number) {
    const tabParam = this.getTabParamFromIndex(tabIndex);
    if (tabParam) {
      this.router.navigate([], {
        relativeTo: this.actRoute,
        queryParams: { tab: tabParam },
        queryParamsHandling: 'merge'
      });
    }
  }

  private getTabParamFromIndex(tabIndex: number): string {
    switch (tabIndex) {
      case 0:
        return 'all';
      case 1:
        return 'draft';
      case 2:
        return 'active';
      case 3:
        return 'completed';
      case 4:
        return 'inactive'
      default:
        return '';
    }
  }

  private getDetailTabParamFromIndex(tabIndex: number): string {
    switch (tabIndex) {
      case 0:
        return 'overview';
      case 1:
        return 'products';
      case 2:
        return 'purchase-orders';
      case 3:
        return 'orders';
      case 4:
        return 'sites';
      case 5:
        return 'sponsors';
      case 6:
        return 'protocol-users';
      case 7:
        return 'activity-feed';
      default:
        return 'overview';
    }
  }
}

